import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { DATE_RANGE, POSTED_END_DATE, POSTED_START_DATE, VENDOR } from "./consts";
import { useGetVendorBalanceReport, useGetVendorListByCommunityId } from "@/services";
import moment from "moment";
import { formatAmount, getRequiredFields } from "@/lib/utils";
import { Button } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";

const useReportByOwner = () => {
  const {communityData, currentFinanceYear} = useSelector((state: RootState) => state.currentData);
  const communityName = communityData?.name || communityData?.communityName || "";
  const isRunBtnClicked = useRef<boolean>(false);
  const [tableData, setTableData] = useState<any>([]);
  const [isFilterList, setIsFilterList] = useState<boolean>(false);
  
  const form = useForm<formValues>({
    defaultValues: {
      [VENDOR]: [],
      [DATE_RANGE]: {from: null, to: null},
    },
    resolver: zodResolver(schema)
  });
  
  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: "",
        name: <span className="text-primary">Report By Vendor</span>,
      },
    ],
    [communityName]
  );

  const title = useMemo(() => <span>{`Report By Vendor - ${communityName}`}<span className="text-[12px]">(FY-{currentFinanceYear})</span></span>,[communityName]);

  const formData = useWatch({control: form.control});

  const {data: vendorList, isLoading: isListLoading} = useGetVendorListByCommunityId({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const {
    data: vendorReport,
    isLoading: isReportLoading,
    refetch: refetchReport,
    isRefetching: isReportRefetching,
  } = useGetVendorBalanceReport({
    data: {
      communityId: communityData?.communityId,
      budgetYear: currentFinanceYear,
      ...(formData[VENDOR] ? {[VENDOR]: formData[VENDOR]} : {}),
      ...(formData[DATE_RANGE]?.from &&
        formData[DATE_RANGE]?.to
        ? {
            [POSTED_START_DATE]: moment(form.watch()[DATE_RANGE]?.from).format(
              "YYYY-MM-DDT00:00:00"
            ),
            [POSTED_END_DATE]: moment(form.watch()[DATE_RANGE]?.to).format(
              "YYYY-MM-DDT23:59:59"
            )
          }
        : {}),
    },
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (vendorReport?.data) {
      let paymentData =
        vendorReport?.data?.glAccountData?.map((data: any) => ({
          ...data,
          children:
            data?.glAccountMonthlyData?.map((gl: any) => ({
              ...gl,
              glAccountId: data?.glAccountId,
            })) || [],
          id: data?.glAccountId,
        })) || [];

      if (isFilterList) {
        paymentData = paymentData?.filter((data: any) => data?.committedAmount || data?.actualAmount)
      }

      if (JSON.stringify(paymentData) != JSON.stringify(tableData)) {
        setTableData(paymentData);
      }
    }
  }, [vendorReport, isFilterList]);

  const onRunReport = () => {
    isRunBtnClicked.current = true;
    refetchReport();
  }

  const onClear = () => {
    form.reset({
      [VENDOR]: [],
      [DATE_RANGE]: {from: null, to: null},
    })
    isRunBtnClicked.current = false;
  }

  // Define Table Columns
  const columns = [
    {
      accessorKey: "glAccount",
      header: <div>GL Account</div>,
    },
    {
      accessorKey: "budgetAmount",
      meta: { align: "right" },
      header: <div>Budget Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.budgetAmount || "0"),
    },
    {
      accessorKey: "committedAmount",
      meta: { align: "right" },
      header: <div>Committed Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.committedAmount || "0"),
    },
    {
      accessorKey: "varCom",
      header: <div>Var-Com</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.budgetAmount - row?.committedAmount || "0"),
    },
    {
      accessorKey: "actualAmount",
      meta: { align: "right" },
      header: <div>Actuals</div>,
      accessorFn: (row: any) => formatAmount(row?.actualAmount || "0"),
    },
    {
      accessorKey: "varAct",
      header: <div>Var-Act</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.budgetAmount - row?.actualAmount || "0"),
    },
  ];

  // Define Table Columns
  const subColumns = [
    {
      accessorKey: "monthName",
      header: <div>Month</div>,
    },
    {
      accessorKey: "budgetAmount",
      meta: { align: "right" },
      header: <div>Budget Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.budgetAmount || "0"),
    },
    {
      accessorKey: "committedAmount",
      meta: { align: "right" },
      header: <div>Committed Amt.</div>,
      accessorFn: (row: any) => {
        if (!row?.committedAmount) {
          return formatAmount(row?.committedAmount || "0");
        } else {
          return (
            <Button
              variant="ghost"
              className="p-0 h-auto"
              onClick={() => {
                // navigate(`${row.glAccountId}/${row.monthName}`);
              }}
            >
              {formatAmount(row?.committedAmount || "0")}
            </Button>
          );
        }
      },
    },
    {
      accessorKey: "actualAmount",
      meta: { align: "right" },
      header: <div>Actuals</div>,
      accessorFn: (row: any) => {
        if (!row?.actualAmount) {
          return formatAmount(row?.actualAmount || "0");
        } else {
          return (
            <Button
              variant="ghost"
              className="p-0 h-auto"
              onClick={() => {
                // navigate(
                //   `${row.glAccountId}/${row.monthName}/${routes.listing}/all`
                // );
              }}
            >
              {formatAmount(row?.actualAmount || "0")}
            </Button>
          );
        }
      },
    },
  ];

  return {
    breadcrumbData,
    title,
    form,
    vendorList: vendorList,
    isLoading: isListLoading,
    onRunReport,
    onClear,
    isDocLoading: isReportLoading || isReportRefetching,
    columns,
    subColumns,
    isRunBtnClicked: isRunBtnClicked.current,
    requiredFields,
    tableData,
    isFilterList,
    setIsFilterList
  }
}

export default useReportByOwner;
